import React from 'react';
import { MdBuild, MdCancel, MdCheckCircle, MdErrorOutline, MdHelpOutline } from 'react-icons/md';
import { resolutionLines } from '../../Utils/stediUtils';

// Shared pieces of the eligibility and claim status result modals.

export const TONES = {
  success: { bg: 'var(--hc-status-success-bg)', text: 'var(--hc-status-success-text)', icon: MdCheckCircle },
  danger: { bg: 'var(--hc-status-danger-bg)', text: 'var(--hc-status-danger-text)', icon: MdCancel },
  warning: { bg: 'var(--hc-status-warning-bg)', text: 'var(--hc-status-warning-text)', icon: MdErrorOutline },
  neutral: { bg: 'var(--hc-status-unknown-bg)', text: 'var(--hc-status-unknown-text)', icon: MdHelpOutline },
};

export const PANEL_STYLE = { background: 'var(--hc-tint-1)', borderColor: 'var(--hc-surface-border)' };
export const MUTED = { color: 'var(--hc-text-muted)' };
export const TITLE = { color: 'var(--hc-text-title)' };
const KICKER = { ...MUTED, letterSpacing: '0.06em', fontSize: '0.72rem' };

export const Kicker = ({ children, aside }) => (
  <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
    <span className="fw-bold text-uppercase" style={KICKER}>{children}</span>
    {aside}
  </div>
);

// Full-width outcome band: tone icon, headline and explanation, with the payer on the right.
export function ResultBanner({ tone, label, detail, payerName, payerId }) {
  const { bg, text, icon: ToneIcon } = TONES[tone];
  return (
    <div
      className="rounded-4 p-3 p-md-4 d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3"
      style={{ background: bg, color: text }}
    >
      <div className="d-flex align-items-center gap-3" style={{ minWidth: 0 }}>
        <span
          className="d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0 shadow-sm"
          style={{ width: 52, height: 52, background: 'var(--hc-surface)' }}
        >
          <ToneIcon size={30} />
        </span>
        <div style={{ minWidth: 0 }}>
          <div className="fw-bold fs-5 lh-sm">{label}</div>
          {detail && <div className="small fw-semibold" style={{ opacity: 0.85 }}>{detail}</div>}
        </div>
      </div>
      {(payerName || payerId) && (
        <div className="text-md-end flex-shrink-0">
          <div className="fw-bold text-uppercase" style={{ ...KICKER, color: text, opacity: 0.75 }}>Payer</div>
          <div className="fw-bold">{payerName || payerId}</div>
          {payerName && payerId && <div className="small" style={{ opacity: 0.8 }}>ID {payerId}</div>}
        </div>
      )}
    </div>
  );
}

// A payer's errors as numbered fixes; the first error already heads the banner, later ones keep their description.
export function FixSteps({ errors, tone }) {
  const { bg, text } = TONES[tone];
  return errors.map((error, index) => {
    const lines = resolutionLines(error.possibleResolutions);
    const steps = lines.length ? lines : [error.followupAction || 'Check the details and resubmit.'];
    return (
      <section key={`${error.code}-${index}`}>
        <Kicker
          aside={error.code && (
            <span className="badge rounded-pill px-3 py-2" style={{ background: bg, color: text }}>Code {error.code}</span>
          )}
        >
          <MdBuild size={14} className="me-1" />
          {index === 0 ? 'How to fix' : error.description || 'Payer error'}
        </Kicker>
        <div className="rounded-4 border p-3 row g-3 m-0" style={PANEL_STYLE}>
          {steps.map((line, step) => (
            <div key={line} className="col-12 col-md-6 d-flex align-items-start gap-2">
              <span
                className="d-inline-flex align-items-center justify-content-center rounded-circle fw-bold flex-shrink-0"
                style={{ width: 24, height: 24, fontSize: '0.75rem', background: bg, color: text }}
              >
                {step + 1}
              </span>
              <span className="small lh-base" style={{ color: 'var(--hc-body-text)' }}>{line}</span>
            </div>
          ))}
        </div>
      </section>
    );
  });
}

export const Detail = ({ label, value }) => (
  <div className="col-6 col-md-4 col-lg" style={{ minWidth: 0 }}>
    <div className="small" style={MUTED}>{label}</div>
    <div className="small fw-semibold text-break" style={TITLE}>{value || '-'}</div>
  </div>
);
